// User Settings Config Builder - Auto-derives defaults and types from schema
// Eliminates duplication between UserSettings interface and SettingConfig definitions

import { DEFAULT_USER_SETTINGS } from '../../types/userSettingsDefinition';
import { BackendDataType, ControlType, UserSettingSchema, UserSettingConfig } from '../../types/settingsTypes';
import { getNestedValue } from './settingsHelpers';

/**
 * Infers backend data type from control type.
 * Maps UI control types to backend API data types.
 */
const inferTypeFromControl = (controlType: ControlType): BackendDataType => {
    switch (controlType) {
        case 'checkbox':
        case 'switch':
            return 'bool';
        case 'dualInput':
        case 'numberInput':
            return 'number';
        case 'multiCheckbox':
            return 'object';
        case 'textbox':
        case 'multiSelect':
            return 'list';
        default:
            return 'string';
    }
};

/**
 * Builds a UserSettingConfig from a UserSettingSchema by auto-populating:
 * - `default`: Looked up from DEFAULT_USER_SETTINGS using the schema's key path
 * - `type`: Inferred from the controlType
 * - `isClientConfig`: Always set to true for user settings
 * 
 * This eliminates manual duplication of defaults and types in config definitions.
 * 
 * @param schema - User setting schema with UI metadata (no default/type)
 * @returns Complete UserSettingConfig ready for use in settings store
 */
export const buildUserSettingConfig = <T extends UserSettingSchema>(
    schema: T
): UserSettingConfig => {
    const defaultValue = getNestedValue(DEFAULT_USER_SETTINGS, schema.key);
    const type = inferTypeFromControl(schema.controlType);
    
    return {
        ...schema,
        type,
        default: defaultValue,
        isClientConfig: true as const,
    } as UserSettingConfig;
};
